import { CHART_EXPORT_RECORD_TYPES, type ChartExportRecordType } from '@emr/shared';
import { useMutation } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { exportChart } from '../../../api/chartExport.ts';
import { errorMessage, isContractMismatch } from '../../../api/client.ts';
import { FailureNotice } from '../../../components/AsyncState.tsx';
import { Button, ErrorText, Field, Panel } from '../../../components/primitives.tsx';

/** A human label for each of `@emr/shared`'s `CHART_EXPORT_RECORD_TYPES`. `Record<..., string>` is
 * the forcing function, matching `VitalsTab.tsx`'s own `vitalTypeLabels` — a record type added to
 * the shared list without a row here fails the build rather than rendering nothing for it. */
function recordTypeLabels(t: TFunction<'patientChartTabs'>): Record<ChartExportRecordType, string> {
  return {
    problems: t('export.recordTypes.problems'),
    allergies: t('export.recordTypes.allergies'),
    medications: t('export.recordTypes.medications'),
    immunizations: t('export.recordTypes.immunizations'),
    vitals: t('export.recordTypes.vitals'),
    labs: t('export.recordTypes.labs'),
    encounters: t('export.recordTypes.encounters'),
    notes: t('export.recordTypes.notes'),
  };
}

/**
 * Saves `blob` to the user's downloads, through a detached, momentary `<a download>` — the standard
 * browser-download mechanism for a blob this page's own `fetch` call produced client-side, not a
 * link to a remote resource. (This is the real product's own web app, not a sandboxed preview
 * surface — a plain `<a download>` works here the way it does on any ordinary site.)
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Chart export to PDF (`BACKLOG.md` C22, staff-facing half). A record-type picker plus an optional
 * inclusive date range, POSTed to `/api/patients/:patientId/export`, which returns a PDF the browser
 * downloads directly — no preview step, matching this feature's real use (a front-desk/clinical
 * staff member generating a document to fax or file, not something reviewed on-screen first).
 *
 * Reachable only from within `ChartTabs` (`chart:read`-gated, per `PatientChartPage.tsx`'s own
 * split) — the export itself is clinical content, gated identically server-side
 * (`chartExportRoutes.ts`). See that file's own header for the documented gap between this and the
 * front-desk/billing persona named in `BACKLOG.md`'s C22 row: under this codebase's current RBAC
 * model, a role holding only `patient:read` cannot reach this tab at all, since it sits behind the
 * same `chart:read` gate as every other clinical tab.
 */
export function ExportTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  const labels = recordTypeLabels(t);

  const [selected, setSelected] = useState<ReadonlySet<ChartExportRecordType>>(
    () => new Set(CHART_EXPORT_RECORD_TYPES),
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const runExport = useMutation({
    mutationFn: () =>
      exportChart(patientId, {
        recordTypes: [...selected],
        startDate: startDate === '' ? undefined : startDate,
        endDate: endDate === '' ? undefined : endDate,
      }),
    onSuccess: (blob) => {
      downloadBlob(blob, `chart-export-${patientId}.pdf`);
    },
  });

  function toggle(type: ChartExportRecordType): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }

  function handleSubmit(): void {
    setFormError(null);
    if (selected.size === 0) {
      setFormError(t('export.selectAtLeastOne'));
      return;
    }
    if (startDate !== '' && endDate !== '' && startDate > endDate) {
      setFormError(t('export.invalidRange'));
      return;
    }
    runExport.mutate();
  }

  return (
    <Panel title={t('export.title')}>
      <p className="m-0 text-sm text-ink-muted">{t('export.description')}</p>

      <fieldset className="m-0 mt-4 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1 p-0 text-sm font-medium text-ink">
          {t('export.recordTypesLegend')}
        </legend>
        {CHART_EXPORT_RECORD_TYPES.map((type) => (
          <label key={type} className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={selected.has(type)}
              onChange={() => {
                toggle(type);
              }}
              className="h-4 w-4 rounded border-field-line"
            />
            {labels[type]}
          </label>
        ))}
      </fieldset>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={t('export.startDateLabel')}
          type="date"
          value={startDate}
          onChange={(event) => {
            setStartDate(event.target.value);
          }}
          hint={t('export.dateRangeHint')}
        />
        <Field
          label={t('export.endDateLabel')}
          type="date"
          value={endDate}
          onChange={(event) => {
            setEndDate(event.target.value);
          }}
        />
      </div>

      {formError !== null ? <ErrorText message={formError} /> : null}
      {runExport.error !== null ? (
        isContractMismatch(runExport.error) ? (
          <FailureNotice error={runExport.error} />
        ) : (
          <ErrorText message={errorMessage(runExport.error)} />
        )
      ) : null}

      <div className="mt-4 flex justify-end">
        <Button pending={runExport.isPending} onClick={handleSubmit}>
          {runExport.isPending ? t('export.submitting') : t('export.submit')}
        </Button>
      </div>
    </Panel>
  );
}
